import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, ArrowUpRight, PlayCircle } from 'lucide-react';

const NorthwesternUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Northwestern University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#4E2A84] selection:bg-[#4E2A84] selection:text-white">
            {/* Top Header */}
            <div className="bg-[#4E2A84] text-white py-1 px-4 text-[11px] font-bold uppercase tracking-widest flex justify-end gap-6">
                <a href="#" className="hover:text-purple-200">Canvas</a>
                <a href="#" className="hover:text-purple-200">Caesar</a>
                <a href="#" className="hover:text-purple-200">Directory</a>
            </div>

            {/* Navbar */}
            <nav className="bg-white border-b border-gray-100 py-6 px-4 sticky top-0 z-50">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-3xl font-serif font-normal tracking-wide text-[#4E2A84]">{schoolName.split(' ')[0]}</span>
                    </div>

                    <div className="hidden lg:flex gap-10 text-sm font-bold text-[#4E2A84] uppercase tracking-wider">
                         <a href="#" className="hover:underline underline-offset-4 decoration-2">About</a>
                         <a href="#" className="hover:underline underline-offset-4 decoration-2">Admissions</a>
                         <a href="#" className="hover:underline underline-offset-4 decoration-2">Academics</a>
                         <a href="#" className="hover:underline underline-offset-4 decoration-2">Research</a>
                         <a href="#" className="hover:underline underline-offset-4 decoration-2">Campus Life</a>
                    </div>

                    <div className="flex gap-4 items-center">
                        <Search size={20} className="text-[#4E2A84] cursor-pointer" />
                        <div className="lg:hidden text-[#4E2A84]">
                            <Menu size={24} />
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section - Split */}
            <header className="grid grid-cols-1 lg:grid-cols-2 h-auto lg:h-[700px]">
                <div className="bg-[#4E2A84] text-white p-12 md:p-24 flex flex-col justify-center">
                    <h1 className="text-5xl md:text-7xl font-serif mb-8 leading-[1.1]">
                        Take a {schoolName.split(' ')[0]} Direction.
                    </h1>
                    <p className="text-xl md:text-2xl font-light mb-12 opacity-90 leading-relaxed">
                        Where different ways of thinking come together to make the world a better place.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Button className="bg-white text-[#4E2A84] hover:bg-purple-50 font-bold uppercase tracking-widest px-8 h-14 rounded-none text-sm">
                            Apply Today
                        </Button>
                        <Button variant="outline" className="border-white text-white hover:bg-purple-900 font-bold uppercase tracking-widest px-8 h-14 rounded-none text-sm bg-transparent">
                            Plan A Visit
                        </Button>
                    </div>
                </div>
                <div className="relative h-[500px] lg:h-full">
                    <img 
                        src="https://images.unsplash.com/photo-1590579491624-f98f36d4c763?q=80&w=2028&auto=format&fit=crop" 
                        alt="Lakefill" 
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                </div>
            </header>

            {/* "What's Next" - Dynamic Content */}
            <section className="py-24 px-4 md:px-12 max-w-[1400px] mx-auto">
                <h2 className="text-4xl font-serif text-[#4E2A84] mb-16 text-center">What's Next?</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                     <div className="group cursor-pointer">
                         <div className="relative aspect-[3/4] mb-6 overflow-hidden">
                             <img src="https://images.unsplash.com/photo-1544531586-fde5298cdd40?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Feature 1" />
                             <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full">
                                 <ArrowUpRight className="text-[#4E2A84]" />
                             </div>
                         </div>
                         <h3 className="text-2xl font-serif font-bold text-[#4E2A84] mb-3 group-hover:underline decoration-2 underline-offset-4">
                             Global perspective: Students study abroad in 50+ countries
                         </h3>
                         <p className="text-gray-600">Expanding horizons through immersive international programs.</p>
                     </div>

                     <div className="group cursor-pointer mt-0 md:mt-12">
                         <div className="relative aspect-[3/4] mb-6 overflow-hidden">
                             <img src="https://images.unsplash.com/photo-1559136555-930d72f1d3d0?q=80&w=2069&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Feature 2" />
                             <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full">
                                 <PlayCircle className="text-[#4E2A84]" />
                             </div>
                         </div>
                         <h3 className="text-2xl font-serif font-bold text-[#4E2A84] mb-3 group-hover:underline decoration-2 underline-offset-4">
                             Innovation in the arts: Wirtz Center season premiere
                         </h3>
                         <p className="text-gray-600">A look behind the scenes of the upcoming theater production.</p>
                     </div>

                     <div className="group cursor-pointer">
                         <div className="relative aspect-[3/4] mb-6 overflow-hidden">
                             <img src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Feature 3" />
                             <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full">
                                 <ArrowUpRight className="text-[#4E2A84]" />
                             </div>
                         </div>
                         <h3 className="text-2xl font-serif font-bold text-[#4E2A84] mb-3 group-hover:underline decoration-2 underline-offset-4">
                             Driving discovery: Bio-electronics breakthroughs
                         </h3>
                         <p className="text-gray-600">Developing dissolveable medical implants for temporary monitoring.</p>
                     </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#1e1e1e] text-white py-20 px-4 md:px-12">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                    <div>
                        <h2 className="text-3xl font-serif mb-6">{schoolName}</h2>
                        <div className="text-sm text-gray-400 space-y-4 font-bold">
                            <p>633 Clark Street <br/> Evanston, IL 60208</p>
                            <p>Phone: 847-491-3741</p>
                        </div>
                        <div className="flex gap-4 mt-8">
                             <div className="w-8 h-8 bg-gray-700 hover:bg-[#4E2A84] transition-colors rounded-full"></div>
                             <div className="w-8 h-8 bg-gray-700 hover:bg-[#4E2A84] transition-colors rounded-full"></div>
                             <div className="w-8 h-8 bg-gray-700 hover:bg-[#4E2A84] transition-colors rounded-full"></div>
                        </div>
                    </div>
                    
                    <div className="md:col-span-2 flex flex-wrap gap-12 text-sm font-bold uppercase tracking-widest text-[#bbb]">
                        <ul>
                            <li className="mb-4 text-white">Campus Resources</li>
                            <li className="mb-2"><a href="#" className="hover:text-white">Emergency Information</a></li>
                            <li className="mb-2"><a href="#" className="hover:text-white">Campus Map</a></li>
                            <li className="mb-2"><a href="#" className="hover:text-white">Parking</a></li>
                        </ul>
                        <ul>
                            <li className="mb-4 text-white">University</li>
                            <li className="mb-2"><a href="#" className="hover:text-white">Contact</a></li>
                            <li className="mb-2"><a href="#" className="hover:text-white">Careers</a></li>
                            <li className="mb-2"><a href="#" className="hover:text-white">Disclaimer</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto mt-12 pt-8 border-t border-gray-700 text-center text-xs text-gray-500 font-sans">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default NorthwesternUniversity;
